/*
* Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
*/
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {ServiceError} from '@ion/core/src/types/services';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {formatErrorMessage, translatePromptAction} from '@ion/dls/src/utils/ComponentUtils';

export interface DeleteResultPromptProps {
  entityTitle: string;
  onAction: () => void;
  isError?: boolean;
  error?: ServiceError;
  autoHideDuration?: number;
  isSuccess?: boolean;
  isReadOnlyUser?: boolean;
  showAsResponse?: boolean;
};

/**
 * Displays a success/error prompt on deleting any module by passing Entity messages
 *
 * ```typescript
 *
 * import {DeleteResultPrompt} from '@ion/features/src/prompts/DeleteResultPrompt';
 *
 * <DeleteResultPrompt entityTitle={entity}
 *    isError={showError}
 *    isSuccess={showSuccess}
 *    onAction={onClose} />
 *
 * ```
 *
 * @param   {string} entityTitle Success/Error message to be displayed in Prompt
 * @param   {Function} onAction Method to do some action after success prompt is displayed
 * @param   {number} autoHideDuration Duration to hide prompt once it is displayed
 * @param   {boolean} isError To display error prompt with a message and action buttons
 * @param   {boolean} isSuccess To display success prompt with a message and action buttons
 *
 */
export const DeleteResultPrompt = ({
  entityTitle,
  onAction,
  error,
  autoHideDuration = 3000,
  isError = false,
  isSuccess = false,
  isReadOnlyUser = false,
  showAsResponse = false
}: DeleteResultPromptProps) => {
  const [show, setShow] = useState<boolean>(false);

  useEffect(() => {
    setShow(isError || isSuccess);
  }, [isError, isSuccess]);

  const onClose = useCallback(function() {
    setShow(false);
    onAction();
  }, []);

  const defaultMessage = useCallback(function(error, entityTitle) {
    if (showAsResponse) {
      return error;
    }
    let message = translate('common:editors.delete.failure', {entity: (entityTitle)});
    if (!isNil(error)) {
      message = error.length > 1 ? formatErrorMessage(error[0]) : formatErrorMessage(error);
    }
    return message;
  }, [showAsResponse]);

  return (
    <>
      <IonSnackbar show={show && isError}
        message={defaultMessage(error, entityTitle)}
        actionLabel={translatePromptAction('notifications:warning.actionLabel', isReadOnlyUser)}
        onAction={onAction}
        onClose={onClose} />
      <IonSnackbar show={show && isSuccess}
        message={translate('common:editors.delete.success', {entity: (entityTitle)})}
        actionLabel={translate('common:editors.delete.ok')}
        onAction={onClose}
        onClose={onClose}
        autoHideDuration={autoHideDuration} />
    </>
  );
};

export default DeleteResultPrompt;
